/**
 * The one rule every stack of panels on the platform follows (operator,
 * 2026-09-13: "in any interface like a tab where you have an accordion ui,
 * only open one at a time, example configs in onu detail, do the same across
 * the platform"): a group holds ONE open key. The Configs tab used to let
 * every panel open on its own — WAN, LAN, WiFi and four sections all
 * expanded is a page nobody scrolls past, and the panel the operator came
 * for is somewhere in the middle of it.
 *
 * Pure, so the rule is pinned in node; `CollapsibleGroup` (components/ui)
 * and the native `<details>` stacks only apply it.
 */

/**
 * The open key after a click: clicking the open panel closes it (nothing
 * open), clicking any other opens that one — and, by the same assignment,
 * closes whichever was open.
 */
export function nextOpenKey(current: string | null, clicked: string): string | null {
  return current === clicked ? null : clicked;
}

/**
 * The open key after a panel asks to start open (`defaultOpen`, the
 * overview's deep link into one editor). The FIRST claim wins and later
 * ones are ignored — WAN and the chassis's "WAN over OMCI" fallback both ask
 * when the overview linked to the WAN, and only the linked one may open —
 * and a claim never overrides a choice the operator already made: a panel
 * that mounts late (its data arrived after the page) must not snap the
 * accordion away from the one they opened.
 */
export function openKeyAfterClaim(
  current: string | null,
  alreadyDecided: boolean,
  claimant: string,
): string | null {
  return alreadyDecided ? current : claimant;
}

/**
 * Which Configs panel starts open. The overview's deep link (`focus`, which
 * IS a panel id — wan, lan, wifi) always wins; with no link the chassis's
 * "WAN over OMCI" fallback opens when it is shown at all — the ACS view is
 * blank then, so the fallback is the WAN — and otherwise nothing starts open.
 * Decided in ONE place rather than by each panel's own `defaultOpen`: with
 * a claim per panel the group took the first claim in DOM order, and the
 * fallback sits above LAN and WiFi, so "Configure: LAN" on a unit whose ACS
 * WAN was empty (a bridged or management-only CPE, a never-read tree)
 * landed on Configs with the chassis WAN open and LAN shut (review,
 * 2026-09-13). On main every panel kept its own state and LAN opened.
 */
export function configsInitialOpenKey(focus: string | null, chassisWanShown: boolean): string | null {
  if (focus) return focus;
  return chassisWanShown ? 'wan-omci' : null;
}

/**
 * Whether a panel's body stays mounted while it is shut: it does while it
 * holds an edit the operator has not applied. The group closes a panel the
 * moment another opens, and a body that unmounts takes its draft with it —
 * a dirty WAN form (a VLAN picked, PPPoE credentials typed) gone to a glance
 * at the LAN card, with no warning (review, 2026-09-13; before the group the
 * second panel opened beside the dirty one). An open panel is mounted as
 * ever; a clean shut one is not, so a closed card still costs no query.
 */
export function panelBodyMounted(open: boolean, dirty: boolean): boolean {
  return open || dirty;
}
